<template>
  <q-layout
    id="app-layout"
    view="hHh LpR lff"
    :class="keyboardIsOpen ? 'keyboard-is-open' : ''"
  >
    <q-layout-header ref="layoutHeader">
      <q-toolbar :color="$env === 'production' ? 'primary' : 'red'">
        <q-btn
          v-if="$route.meta.previousPage && $q.screen.lt.md"
          flat
          round
          dense
          icon="arrow_back_ios"
          :to="previousPageTo"
          color="white"
        />
        <q-btn
          v-if="menuItems.length"
          flat
          round
          dense
          icon="menu"
          @click="drawers.left = !drawers.left"
        />
        <q-toolbar-title>
          <div class="row gutter-x-sm items-center">
            <div class="col-auto">
              <router-link :to="{ name: 'app.activities' }">
                <bj-icon
                  v-if="$q.screen.lt.lg"
                  :width="45"
                  :height="45"
                />
                <bj-logo
                  v-else
                  type="inverted"
                  :height="45"
                />
              </router-link>
            </div>
            <div
              v-if="$env !== 'production' && $q.screen.gt.sm"
              class="col-auto"
            >
              <q-chip
                small
                color="white"
                text-color="primary"
              >
                {{ $env.toUpperCase() }}
              </q-chip>
            </div>
            <div
              v-if="$q.screen.gt.sm && userRoleIs($config.roles.ADMIN, $config.roles.SUPER_ADMIN)"
              class="col-auto"
            >
              <q-chip
                small
                color="white"
                text-color="secondary"
              >
                ADMIN
              </q-chip>
            </div>
          </div>
        </q-toolbar-title>
        <div class="row gutter-x-sm items-center">
          <div class="col-auto hidden">
            <div class="row gutter-x-xs">
              <div
                v-for="(menuItem, index) in menuItems"
                :key="index"
                class="col-auto"
              >
                <q-btn
                  v-if="menuItem.children == undefined"
                  :label="menuItem.label"
                  :flat="!isCurrentPage(menuItem)"
                  :color="isCurrentPage(menuItem) ? 'white' : ''"
                  :text-color="isCurrentPage(menuItem) ? 'secondary' : 'white'"
                  rounded
                  @click.native="redirect(menuItem.route)"
                />
                <q-btn-dropdown
                  v-else
                  :label="menuItem.label"
                  :flat="!isCurrentPage(menuItem)"
                  :color="isCurrentPage(menuItem) ? 'white' : ''"
                  :text-color="isCurrentPage(menuItem) ? 'secondary' : 'white'"
                  rounded
                >
                  <q-list link>
                    <q-item
                      v-for="(childItem, index) in menuItem.children"
                      :key="index"
                      v-close-overlay
                      @click.native="redirect(childItem.route)"
                    >
                      <q-item-main>
                        <q-item-tile label>
                          {{ childItem.label }}
                        </q-item-tile>
                      </q-item-main>
                    </q-item>
                  </q-list>
                </q-btn-dropdown>
              </div>
            </div>
          </div>
          <div
            v-if="userRoleIs($config.roles.SUPER_ADMIN, $config.roles.ADMIN)"
            class="col-auto"
          >
            <WideSearch
              v-if="$q.screen.gt.sm"
              class="d-flex align-items-center"
              style="min-width: 450px"
            />
            <q-btn
              v-else
              icon="search"
              flat
              round
              color="white-4"
              @click="showSearchToolbar = !showSearchToolbar"
            />
          </div>
          <div
            v-if="userRoleIs($config.roles.AGENT)"
            class="col-auto"
          >
            <a :href="`tel:${getSetting('support_phone_number')}`">
              <q-btn
                v-tippy
                round
                color="white"
                text-color="green"
                icon="call"
                title="Call support"
              />
            </a>
          </div>
          <div class="col-auto">
            <div class="row items-center">
              <div class="col-auto">
                <!--<img id="toolbar-user-avatar" :src="$store.getters['auth/user'].avatar_file_url" alt="" height="42">-->
                <img
                  id="toolbar-user-avatar"
                  :src="avatarUrl"
                  alt=""
                  height="42"
                >
              </div>
              <div class="col-auto">
                <q-btn
                  flat
                  rounded
                  no-caps
                >
                  <q-icon
                    name="arrow_drop_down"
                    :class="$q.screen.gt.sm ? 'q-mr-sm' : ''"
                  />
                  <div
                    class="text-left"
                    v-html="getHeaderDropdownLabel"
                  />
                  <q-popover fit>
                    <q-list
                      link
                      no-border
                    >
                      <q-list-header v-html="$q.screen.gt.sm ? 'User' : get($store.getters['auth/user'], 'full_name')" />
                      <q-item
                        v-close-overlay
                        @click.native="redirect('app.profile')"
                      >
                        <q-item-main>
                          <q-item-tile label>
                            Profile
                          </q-item-tile>
                        </q-item-main>
                      </q-item>
                      <q-item
                        v-if="userRoleIs($config.roles.ACCOUNT_OWNER, $config.roles.MEMBER)"
                        v-close-overlay
                        @click.native="redirect('app.business.team')"
                      >
                        <q-item-main>
                          <q-item-tile label>
                            Team
                          </q-item-tile>
                        </q-item-main>
                      </q-item>
                      <q-item
                        v-close-overlay
                        @click.native="logout"
                      >
                        <q-item-main>
                          <q-item-tile label>
                            Sign out
                          </q-item-tile>
                        </q-item-main>
                      </q-item>
                    </q-list>
                  </q-popover>
                </q-btn>
              </div>
            </div>
          </div>
        </div>
      </q-toolbar>
      <q-toolbar v-if="showSearchToolbar">
        <WideSearch
          class="d-flex align-items-center full-width"
          @selected="showSearchToolbar = false"
        />
      </q-toolbar>
    </q-layout-header>
    <q-layout-drawer
      v-if="menuItems.length"
      v-model="drawers.left"
      side="left"
      class="bg-primary"
    >
      <div class="column justify-between full-height">
        <div class="col overflow-auto">
          <q-list
            separator
            link
            no-border
            class="q-pb-none"
          >
            <div
              v-for="(item, index) in menuItems"
              :key="item.label"
            >
              <q-collapsible
                v-if="item.children"
                :label="item.label"
                :icon="item.icon || 'home'"
                :opened="isCurrentPage(item)"
                collapse-icon="keyboard_arrow_down"
                header-class="drawer-menu-item"
              >
                <q-list
                  link
                  no-border
                >
                  <router-link
                    v-for="childItem in item.children"
                    :key="childItem.route"
                    :to="{ name: childItem.route }"
                    class="main-nav-link"
                  >
                    <q-item
                      class="drawer-menu-item drawer-submenu-item"
                      :class="{ 'q-item-selected': isCurrentPage(childItem) }"
                      @click.native="menuItemClicked(childItem)"
                    >
                      <q-item-side :icon="childItem.icon || 'chevron_right'" />
                      <q-item-main>
                        <q-item-tile label>
                          {{ childItem.label }}
                        </q-item-tile>
                      </q-item-main>
                    </q-item>
                  </router-link>
                </q-list>
              </q-collapsible>
              <router-link
                v-else
                :to="{ name: item.route }"
                class="main-nav-link"
              >
                <q-item
                  :key="index"
                  class="drawer-menu-item"
                  :class="{ 'q-item-selected': isCurrentPage(item) }"
                  @click.native="menuItemClicked(item)"
                >
                  <q-item-side :icon="item.icon || 'home'" />
                  <q-item-main>
                    <q-item-tile label>
                      {{ item.label }}
                    </q-item-tile>
                  </q-item-main>
                </q-item>
              </router-link>
            </div>
          </q-list>
        </div>
        <div
          id="drawer-essential-links"
          class="col-auto q-pa-sm"
        >
          <div class="row gutter-x-sm gutter-y-xs">
            <div class="col-auto">
              <a
                href="https://www.showdigs.com/privacy"
                target="_blank"
              >Privacy Policy</a>
            </div>
            <div class="col-auto">
              <a
                href="https://www.showdigs.com/terms"
                target="_blank"
              >Terms of Use</a>
            </div>
            <div class="col-12">
              <small style="font-size:12px;">
                Bluejay Labs, Inc. All rights reserved © {{ (new Date).getFullYear() }}
                <br>
                App version: v{{ $config.appVersion }}
              </small>
            </div>
          </div>
        </div>
      </div>
    </q-layout-drawer>

    <q-page-container>
      <q-page :class="{ 'no-padding': $route.meta.noPadding }">
        <div
          v-if="$route.meta.previousPage && $q.screen.gt.sm"
          class="q-mb-md"
        >
          <router-link
            id="back-button-desktop"
            :to="previousPageTo"
          >
            <q-icon name="arrow_back_ios" />{{ $route.meta.previousPage.label }}
          </router-link>
        </div>
        <div
          v-if="!$route.meta.noTitle || $route.meta.title"
          id="page-title"
          class="q-mb-md"
          v-html="computedPageHeaderTitle"
        />
        <router-view :key="$route.fullPath" />
        <q-page-sticky
          v-if="userRoleIs($config.roles.ACCOUNT_OWNER, $config.roles.MEMBER) && !$route.name.startsWith('app.properties.')"
          position="bottom-right"
          :offset="[18, 18]"
        >
          <q-fab
            color="secondary"
            icon="add"
            direction="up"
          >
            <q-fab-action
              color="secondary"
              icon="library_books"
              @click="openAddInquiryModal"
            >
              <q-tooltip
                anchor="center left"
                self="center right"
                :offset="[20, 0]"
              >
                Add Inquiry
              </q-tooltip>
            </q-fab-action>
            <q-fab-action
              color="secondary"
              icon="home"
              @click="redirect('app.properties.add')"
            >
              <q-tooltip
                anchor="center left"
                self="center right"
                :offset="[20, 0]"
              >
                Add Property
              </q-tooltip>
            </q-fab-action>

            <bj-add-inquiry-modal ref="addInquiryModal" />
          </q-fab>
        </q-page-sticky>
      </q-page>
    </q-page-container>
  </q-layout>
</template>

<script>
import get from 'lodash.get';
import WideSearch from '../components/wide-search';
import BjAddInquiryModal from '../components/add-inquiry-modal';

export default {
  components: {
    WideSearch,
    BjAddInquiryModal,
  },
  data() {
    return {
      get,
      drawers: {
        left: false,
      },
      showSearchToolbar: false,

      // PM ONLY
      addInquiryModal: {
        loader: null,
        visible: null,
        fields: null,
        payload: null,
        basePayload: null,
      },

      keyboardIsOpen: false,
      pageHeaderTitle: null,
      avatarUrl: '',
    };
  },
  computed: {
    computedPageHeaderTitle() {
      return this.$route.meta.title || this.$route.meta.name || this.pageHeaderTitle;
    },
    previousPageTo() {
      if (this.$route.meta.previousPage) {
        const previousPage = {
          name: this.$route.meta.previousPage.name,
          params: {},
        };

        if (this.$route.meta.previousPage.params) {
          this.$route.meta.previousPage.params.forEach((param) => {
            if (this.$route.params[param]) {
              previousPage.params[param] = this.$route.params[param];
            }
          });
        }

        return previousPage;
      }
      return false;
    },
    getHeaderDropdownLabel() {
      if (!this.$store.getters['auth/user']) {
        return '';
      }
      let label = this.$store.getters['auth/user'].full_name;
      if (this.userRoleIs(this.$config.roles.ACCOUNT_OWNER, this.$config.roles.MEMBER) && this.$q.screen.gt.sm) {
        label = `<strong>${label}</strong>`;
        const business = this.myBusiness();
        if (business && business.name) {
          label += `<br>${business.name}`;
        }
      }
      return label;
    },
    menuItems() {
      const menuItems = [
        {
          label: 'Daily Activities',
          subLabel: 'Daily Activities',
          icon: 'dns',
          route: 'app.activities',
        },
        {
          label: 'Map',
          subLabel: 'Map overview',
          icon: 'map',
          route: 'app.map',
        },
        {
          label: 'Properties',
          subLabel: 'Manage Properties',
          icon: 'home',
          route: 'app.properties.combined',
        },
        {
          label: 'Subscriptions',
          subLabel: 'Manage subscriptions',
          icon: 'business_center',
          route: 'app.businesses',
        },
        {
          label: 'Agents',
          subLabel: 'Manage agents',
          icon: 'group',
          route: 'app.agents',
        },
        {
          label: 'Inquiries',
          subLabel: 'Manage inquiries',
          icon: 'library_books',
          route: 'app.inquiries',
        },
        {
          label: 'Leads',
          subLabel: 'Manage leads',
          icon: 'pages',
          route: 'app.leads',
        },
        {
          label: 'Tours',
          subLabel: 'View tours',
          icon: 'fa fa-sign',
          route: 'app.tours',
        },
        {
          label: 'Transactions',
          subLabel: '',
          icon: 'credit_card',
          route: 'app.transactions',
        },
        {
          label: 'Condition Reports',
          subLabel: 'View condition reports',
          icon: 'credit_card',
          route: 'app.inspections',
        },
        {
          label: 'Markets',
          subLabel: 'Manage markets',
          icon: 'fa fa-store',
          route: 'app.markets.index',
        },
        {
          label: 'Surveys',
          subLabel: 'Manage surveys',
          icon: 'format_list_bulleted',
          route: 'app.surveys.index',
        },
        {
          label: 'Inquiry Sources',
          subLabel: 'Manage inquiry sources',
          icon: 'map',
          route: 'app.inquiry-sources',
        },
        {
          label: 'Operating Hours',
          subLabel: 'Manage operating hours',
          icon: 'access_time',
          route: 'app.operating-hours',
        },
        {
          label: 'PMS Integration',
          subLabel: '',
          icon: 'content_copy',
          route: 'app.business-sources',
        },
        {
          label: 'Settings',
          subLabel: 'Define app settings',
          icon: 'settings',
          route: 'app.settings',
        },
        {
          label: 'Prices',
          subLabel: 'Manage app prices',
          icon: 'credit_card',
          route: 'app.prices',
        },
        {
          label: 'Devices',
          subLabel: 'Manage devices',
          icon: 'settings',
          route: 'app.devices',
        },
        {
          label: 'Dev Tools',
          subLabel: 'Internal investigation tools',
          icon: 'build',
          children: [
            {
              label: 'Scheduling Investigation',
              icon: 'device_hub',
              route: 'app.dev-tools.scheduling-investigation',
            },
            {
              label: 'Spams',
              icon: 'report',
              route: 'app.dev-tools.spams',
            },
            ...(this.$env !== 'production' ? [{
              label: 'AI Playground',
              icon: 'fa fa-play',
              route: 'app.dev-tools.ai-playground',
            }] : []),
          ],
        },
      ];

      if (this.userRoleIs(this.$config.roles.SUPER_ADMIN)) {
        menuItems.splice(4, 0, {
          label: 'Admins',
          subLabel: 'Manage Admins',
          icon: 'supervised_user_circle',
          route: 'app.admins',
        });
      }

      return menuItems;
    },
  },
  created() {
    this.api()
      .request('GET', 'admin/me')
      .then((user) => {
        this.avatarUrl = user.avatar_file_url;
        if (![this.$config.roles.SUPER_ADMIN, this.$config.roles.ADMIN].includes(user.role)) {
          this.logout(false);
        }
        this.$store.commit('auth/SET_USER', user);
      }).catch(() => {
        this.logout(false);
      });

    this.api()
      .request('GET', 'settings')
      .then((settings) => {
        settings = settings.map((setting) => ({
          key: setting.key,
          value: setting.value,
          children: setting.children.map((s) => ({
            key: s.key,
            value: s.value,
            market_id: s.market_id,
          })),
        }));
        this.$store.commit('app/SET_SETTINGS', settings);
      });

    document.addEventListener('deviceready', () => {
      this.updateDevice();
    }, false);

    this.updateDevice();
  },
  mounted() {
    const setHeaderHeightVar = () => {
      if (!this.$refs.layoutHeader) {
        return;
      }
      document.documentElement.style.setProperty('--header-height', `${this.$refs.layoutHeader.$el.offsetHeight}px`);
    };
    this.$nextTick(() => {
      setHeaderHeightVar();
    });
    window.addEventListener('resize', setHeaderHeightVar);
    this.$root.$on('update-page-header-title', (title) => {
      this.pageHeaderTitle = title;
      document.title = `Admin ${title}`;
    });
  },
  methods: {
    isCurrentPage(menuItem) {
      if (menuItem.route) {
        return this.$route.name.startsWith(menuItem.route);
      }
      if (menuItem.children && menuItem.children.length > 0) {
        return menuItem.children.map((child) => child.route).includes(this.$route.name);
      }
      return false;
    },
    updateDevice() {
      if (this.userRoleIs(this.$config.roles.AGENT)) {
        const payload = {
          app_version: this.$config.appVersion,
        };
        if (typeof device !== 'undefined') {
          payload.device = device;
        } else {
          payload.device = {
            user_agent: navigator.userAgent,
          };
        }
        this.api().request('PUT', 'update-device', payload).then((user) => {
          this.$store.commit('auth/SET_USER', user);
        }).catch(() => {});
      }
    },
    openAddInquiryModal() {
      this.$refs.addInquiryModal.show();
    },
    menuItemClicked(item) {
      if (item.route) {
        this.redirect(item.route);
      }
      if (item.handler) {
        item.handler();
      }
      this.drawers.left = false;
    },
  },
};
</script>
